/**
 * «Sono dentro una gara che sta girando?», in una forma sola per tutta l'app.
 *
 * Specchio lato client di `isInCompetition` del server
 * (packages/server/src/lobby/competition-guard.ts), che e' l'autorita' vera: la'
 * la stessa domanda si risolve incrociando i socket e gli iscritti delle gare in
 * corso, su piu' schede e piu' dispositivi. Qui serve a tre cose che stanno
 * tutte DA QUESTA PARTE dello schermo — mostrare un banner, tenere acceso lo
 * schermo, spegnere un bottone prima che venga premuto — e un disaccordo col
 * server si risolve sempre col rifiuto del server, che continua ad arrivare.
 *
 * La regola e' quella decisa per la lobby il 10 ottobre, e qui si ripete
 * identica perche' e' la stessa domanda:
 *  - **prima dell'inizio** si gioca liberamente: iscritti a un torneo o a
 *    un'arena non ancora partiti si puo' restare per ore;
 *  - **a gara in corso** si e' dentro, anche fra un turno e l'altro;
 *  - **a gara finita** si e' fuori subito.
 *
 * Pura e fuori dai componenti per la stessa ragione di `isSafeToReload`: e' una
 * congiunzione lunga che governa tre comportamenti diversi, e un termine
 * sbagliato non si vedrebbe a schermo.
 */

/** Gli stati del torneo in cui si e' dentro una gara che gira. */
const TORNEO_IN_CORSO = new Set(['waiting', 'round-waiting', 'match-found', 'playing']);

/**
 * Gli stati dell'arena, idem — e `'lobby'` NON e' qui dentro.
 *
 * Il criterio, che e' la riga da leggere prima di allargare questo elenco:
 * questi quattro stati sono **gia' una conferma** che l'arena gira, perche'
 * nessuno li scrive senza aver visto una prova fresca — `joinArena` e
 * `loadArena` pretendono `status === 'active'` sul record, `setArenaStarted` e'
 * l'annuncio di partenza, `setArenaResumed` e' un ack del server, e
 * `matchFound`/`playing`/`gameEnd` vogliono un accoppiamento vero.
 *
 * `'lobby'` invece e' lo stato NEUTRO — «nessuna partita a schermo» — e lo
 * scrive a occhi chiusi anche l'inizio di ogni `loadArena`: vale sia per chi e'
 * in pausa dentro un'arena viva (e li' si e' dentro eccome) sia per chi si e'
 * appena iscritto a un'arena di stasera (e li' si gioca liberamente, come il
 * server). Da solo non dice quale dei due, e `arenaIscrittaId` non basta a
 * distinguerli: serve una conferma a parte, `arenaIscrittaAttiva`.
 */
const ARENA_IN_CORSO = new Set(['searching', 'matchFound', 'playing', 'gameEnd']);

export interface StatoGara {
  /** `useTournamentStore.isJoined`. */
  torneoIscritto: boolean;
  /** `useTournamentStore.status`. */
  torneoStato: string;
  /**
   * `useArenaStore.arenaIscrittaId`, e NON `isJoined` piu' `status`.
   *
   * Lo store dell'arena tiene tre nomi distinti apposta — quella che si GUARDA
   * (`currentArena`), quella a cui si e' ISCRITTI (questa) e quella in cui si
   * GIOCA (`arenaPartitaId`) — perche' iscriversi a un'arena e poi aprirne
   * un'altra dall'elenco e' permesso di proposito. In quel caso `status`
   * racconta l'arena GUARDATA, quindi da solo mentirebbe. Questo campo ha
   * un'invariante dichiarata nello store: si scrive e si azzera insieme a
   * `isJoined`.
   */
  arenaIscrittaId: string | null;
  /** `useArenaStore.status`. */
  arenaStato: string;
  /** `useArenaStore.myParticipant?.status === 'paused'`. */
  arenaInPausa: boolean;
  /**
   * Il RECORD dell'arena a cui si e' iscritti dice `status: 'active'`, cioe'
   * quell'arena e' davvero partita. Lo risponde `laMiaArenaEAttiva`, che si usa
   * come selettore.
   *
   * Serve a disambiguare il solo `'lobby'` — vedi `ARENA_IN_CORSO`. Senza di
   * esso, iscriversi a un'arena di stasera accendeva il banner e spegneva la
   * creazione dei tavoli per ore, mentre il server lasciava giocare: era la
   * segnalazione del 21 ottobre.
   */
  arenaIscrittaAttiva: boolean;
}

/** Quale gara sta girando, se ce n'e' una. */
export function garaInCorso(s: StatoGara): 'torneo' | 'arena' | null {
  if (s.torneoIscritto && TORNEO_IN_CORSO.has(s.torneoStato)) return 'torneo';
  if (s.arenaIscrittaId !== null) {
    if (ARENA_IN_CORSO.has(s.arenaStato)) return 'arena';
    /* Lo stato neutro ha bisogno della conferma, gli altri se la portano. */
    if (s.arenaStato === 'lobby' && s.arenaIscrittaAttiva) return 'arena';
  }
  return null;
}

/**
 * «L'arena a cui sono iscritto e' partita?», letta dai record invece di essere
 * copiata in un campo.
 *
 * Si usa come selettore — `useArenaStore(laMiaArenaEAttiva)` — e risponde uno
 * scalare: legge la lista, ma non fa ridisegnare niente piu' di prima. Sta qui
 * e non nello store per la stessa ragione degli altri predicati di questo file:
 * governa un banner, una lobby e lo schermo acceso, e un termine sbagliato non
 * si vedrebbe. I tipi sono strutturali di proposito, cosi' il file resta puro e
 * i test lo chiamano con due oggetti scritti a mano.
 *
 * Due decisioni, entrambe pagate da un difetto vero:
 *
 * 1. **OR fra i due record, non precedenza all'id.** All'avvio il server manda
 *    `arena:started` alla sola stanza `arena:<id>` e `arena:list-updated` a
 *    tutti (`arena.handler.ts`): dopo una riconnessione la stanza non c'e' piu',
 *    arriva solo il broadcast, e `loadArenas()` rinfresca `arenas` SENZA
 *    toccare `currentArena`. Con «l'id combacia, quindi vince `currentArena`»
 *    un record fermo a `'scheduled'` batterebbe quello appena riletto. Basta
 *    che UNO dei due dica `'active'`.
 * 2. **Nessun record trovato: `true`.** Non e' prudenza al rovescio: l'unica
 *    strada che scrive `arenaIscrittaId` senza `currentArena` e' `rejoinGame`,
 *    che lancia la `loadArena` e non l'aspetta — se quella GET fallisce
 *    «`currentArena` resta nullo per tutta la partita» (lo dice gia' il
 *    commento di `setArenaGameCompleted`) — e da li' «In pausa» o
 *    `rilasciaPartita` portano a `'lobby'` DENTRO un'arena viva. Il `false`
 *    sbaglierebbe esattamente dove il server rifiuta comunque (conta anche chi
 *    e' in pausa), cioe' bottone acceso e scusa dopo il giro di rete. La
 *    combinazione opposta non esiste: `reset()` azzera `arenaIscrittaId` e
 *    `currentArena` insieme.
 *
 * ⚠️ NON accorparlo alle due ricerche di record che `arena-store` fa gia'
 * (`joinArena`, `resumeSearch`): hanno il ripiego OPPOSTO — la' un record
 * assente non deve bloccare un'iscrizione ne' una ripresa.
 */
export function laMiaArenaEAttiva(s: {
  arenaIscrittaId: string | null;
  currentArena: { id: string; status: string } | null;
  arenas: { id: string; status: string }[];
}): boolean {
  const id = s.arenaIscrittaId;
  if (id === null) return false;
  const miei = [s.currentArena, ...s.arenas].filter(
    (a): a is { id: string; status: string } => a != null && a.id === id,
  );
  if (miei.length === 0) return true;
  return miei.some((a) => a.status === 'active');
}

/**
 * Quale gara va ANNUNCIATA col banner.
 *
 * Come sopra, meno lo stato `'playing'`: durante la partita della gara c'e' la
 * schermata di partita a dirlo, e un banner sopra la damiera sarebbe rumore
 * davanti a una cosa che si sta gia' guardando.
 */
export function garaDaAnnunciare(s: StatoGara): 'torneo' | 'arena' | null {
  const quale = garaInCorso(s);
  if (quale === 'torneo' && s.torneoStato === 'playing') return null;
  if (quale === 'arena' && s.arenaStato === 'playing') return null;
  return quale;
}

/** Dove si e' adesso: il sottoinsieme di `AppLocation` che serve qui. */
export interface PaginaCorrente {
  screen: string;
  selectedArenaId: string | null;
  selectedTournamentId: string | null;
}

/** Le due gare a cui si e' ISCRITTI, per nome. */
export interface GaraIscritta {
  /** `useTournamentStore.torneoIscrittoId`. */
  torneoIscrittoId: string | null;
  /** `useArenaStore.arenaIscrittaId`. */
  arenaIscrittaId: string | null;
}

/**
 * La pagina aperta e' quella della gara che il banner annuncerebbe.
 *
 * E' il gemello di `'playing'` qui sopra, con lo stesso argomento allargato di
 * una schermata: chi sta GUARDANDO la propria gara non ha bisogno che un banner
 * gliela annunci, e sulla pagina del dettaglio quel banner si posava proprio
 * sopra la banda dei bottoni — «Esci» compreso.
 *
 * Quattro modi di sbagliarla, tutti e quattro provati:
 *
 * 1. **Si confronta con l'id ISCRITTO, mai con quello in pagina.**
 *    `currentArena` NON si azzera quando si lascia la pagina — lo fa solo
 *    `reset()`, che `useArenaResultsCleanup` riserva al podio — quindi
 *    `arenaIscrittaId === currentArena?.id` resta vero anche dalla Home, e come
 *    segnale di «sono sulla sua pagina» direbbe sempre di si'. Lo dice la
 *    schermata, che e' l'unica a saperlo.
 * 2. **Due assenze non sono un incontro.** `torneoIscrittoId` puo' essere
 *    `null` con `isJoined` gia' vero (lo scrive `tournament-store`: subito dopo
 *    l'iscrizione puo' non essere ancora scritto), e `selectedTournamentId` e'
 *    `null` su ogni schermata che non sia un dettaglio: senza la guardia di
 *    non-nullita' il banner sparirebbe dove invece serve.
 * 3. **Si decide su `quale`, non sulla pagina.** Il torneo ha la precedenza
 *    sull'arena in `garaDaAnnunciare`: si puo' essere sulla pagina della
 *    propria arena mentre il banner annuncia il TORNEO, e li' deve restare.
 * 4. **`arena-results` non c'e'.** Per l'arena e' irraggiungibile ('finished'
 *    non sta in `ARENA_IN_CORSO`), e per il torneo sarebbe sbagliato: il podio
 *    di un'arena e' un'altra gara, e il promemoria del torneo deve vedersi.
 *
 * Le schermate di partita ci sono nonostante `'playing'` le copra quasi tutte:
 * ne restano fuori `match-found`, `matchFound`, `gameEnd` e il riepilogo di
 * fine turno, e li' il banner finisce sopra i bottoni della carta di fine
 * partita, che sta a un piano z piu' basso.
 */
export function laPaginaEDellaGara(
  quale: 'torneo' | 'arena',
  iscritta: GaraIscritta,
  p: PaginaCorrente | null,
): boolean {
  if (p === null) return false;

  if (quale === 'torneo') {
    /* Senza confronto di id: in quelle due schermate ci si entra solo dal
       proprio torneo, e `torneoIscrittoId` li' puo' essere ancora vuoto. */
    if (p.screen === 'tournament-lobby' || p.screen === 'tournament-game') return true;
    if (p.screen !== 'tournament-detail') return false;
    return iscritta.torneoIscrittoId !== null
      && p.selectedTournamentId === iscritta.torneoIscrittoId;
  }

  if (p.screen === 'arena-game') return true;
  if (p.screen !== 'arena-detail') return false;
  return iscritta.arenaIscrittaId !== null
    && p.selectedArenaId === iscritta.arenaIscrittaId;
}

/**
 * Si aspetta qualcosa che puo' arrivare da sola: e' quando lo schermo deve
 * restare acceso.
 *
 * Il banner meno l'arena in PAUSA. Chi mette in pausa si e' tolto di proposito
 * dal giro e non puo' essere accoppiato: tenergli acceso lo schermo sarebbe
 * batteria spesa per non aspettare niente. Il torneo non ha una pausa — da un
 * torneo a turni avviato non si esce nemmeno — quindi li' non c'e' niente da
 * togliere.
 */
export function garaTieneAccesoLoSchermo(s: StatoGara): boolean {
  const quale = garaDaAnnunciare(s);
  if (quale === null) return false;
  return !(quale === 'arena' && s.arenaInPausa);
}
